import * as React from "react";
import { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import {
  Alert,
  ControlLabel,
  DropdownButton,
  FormControl,
  FormGroup,
  HelpBlock,
  Input,
  InputGroup,
  MenuItem
} from "components/bootstrap";
import { TimeUnitInput } from "components/common";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import HelpPanel from "components/event-definitions/common/HelpPanel";
import * as FormsUtils from "util/FormsUtils";
import AnomalyQueryInput from "security-app/components/AnomalyEvents/AnomalyQueryInput";
import ImpossibleTravelFieldCheck from "./ImpossibleTravelFieldCheck";
export const initialImpossibleTravelConfig = {
  type: "impossible_travel",
  user_field: "user_name",
  query: null,
  distance_threshold: 900,
  distance_unit: "km",
  time_threshold_ms: 36e5,
  lookback_ms: 864e5
};
const LOOKBACK_UNITS = ["HOURS", "MINUTES", "DAYS"];
const TIME_THRESHOLD_UNITS = ["HOURS", "MINUTES"];
const MS_PER_HOUR = 36e5;
const EMPTY_STRINGS = [];
export const formatSpeed = (distanceThreshold, distanceUnit, timeThresholdMs) => {
  if (timeThresholdMs <= 0 || distanceThreshold <= 0) return "";
  const timeHours = timeThresholdMs / MS_PER_HOUR;
  const speed = distanceThreshold / timeHours;
  const speedRounded = Math.round(speed * 10) / 10;
  if (distanceUnit === "km") {
    return `${speedRounded.toLocaleString()} km/h`;
  }
  return `${speedRounded.toLocaleString()} mph`;
};
const ImpossibleTravelEventForm = ({ eventDefinition, onChange, section = "config" }) => {
  var _a, _b, _c, _d, _e;
  const subtypeConfig = (_a = eventDefinition.config.config) != null ? _a : initialImpossibleTravelConfig;
  const lookback = extractDurationAndUnit(subtypeConfig.lookback_ms, LOOKBACK_UNITS);
  const [lookbackDuration, setLookbackDuration] = useState(lookback.duration);
  const [lookbackUnit, setLookbackUnit] = useState(lookback.unit);
  const timeThreshold = extractDurationAndUnit(subtypeConfig.time_threshold_ms, TIME_THRESHOLD_UNITS);
  const [timeThresholdDuration, setTimeThresholdDuration] = useState(timeThreshold.duration);
  const [timeThresholdUnit, setTimeThresholdUnit] = useState(timeThreshold.unit);
  const speedLabel = formatSpeed(
    subtypeConfig.distance_threshold,
    subtypeConfig.distance_unit,
    subtypeConfig.time_threshold_ms
  );
  const updateSubtypeConfig = (name, value) => {
    var _a2;
    const config = cloneDeep(eventDefinition.config);
    const nextSubtypeConfig = cloneDeep((_a2 = config.config) != null ? _a2 : subtypeConfig);
    nextSubtypeConfig[name] = value;
    config.config = nextSubtypeConfig;
    onChange("config", config);
  };
  const handleSubtypeInputChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    updateSubtypeConfig(name, value);
  };
  const handleLookbackChange = (nextValue, nextUnit) => {
    setLookbackDuration(nextValue);
    setLookbackUnit(nextUnit);
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    updateSubtypeConfig("lookback_ms", durationInMs);
  };
  const handleTimeThresholdChange = (nextValue, nextUnit) => {
    setTimeThresholdDuration(nextValue);
    setTimeThresholdUnit(nextUnit);
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    updateSubtypeConfig("time_threshold_ms", durationInMs);
  };
  const handleQueryChange = (changeEvent) => {
    updateSubtypeConfig("query", changeEvent.target.value);
    return Promise.resolve(changeEvent.target.value);
  };
  if (section === "query") {
    return /* @__PURE__ */ React.createElement(
      AnomalyQueryInput,
      {
        controlId: "impossible-travel-query",
        value: (_b = subtypeConfig.query) != null ? _b : "",
        onChange: handleQueryChange,
        streams: (_c = eventDefinition.config.streams) != null ? _c : EMPTY_STRINGS,
        eventDefinition
      }
    );
  }
  if (section === "sidebar") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(HelpPanel, { collapsible: true, defaultExpanded: true, title: "How does Impossible Travel detection work?" }, /* @__PURE__ */ React.createElement("p", null, "This detector identifies users who appear to log in from two geographically distant locations faster than would be physically possible."), speedLabel && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "An alert will trigger when a user travels at least", " ", /* @__PURE__ */ React.createElement("strong", null, subtypeConfig.distance_threshold.toLocaleString(), " ", subtypeConfig.distance_unit === "miles" ? "miles" : "km"), " ", "at a speed of ", /* @__PURE__ */ React.createElement("strong", null, speedLabel), " or faster."), /* @__PURE__ */ React.createElement("p", null, "The ", /* @__PURE__ */ React.createElement("b", null, "Distance Threshold"), " sets the minimum distance between two logins for the detector to consider them. Shorter distances are ignored, which helps filter out noise from geo-IP inaccuracy, ISP changes, and mobile carrier handoffs."), /* @__PURE__ */ React.createElement("p", null, "The ", /* @__PURE__ */ React.createElement("b", null, "Time Threshold"), " combined with the distance threshold determines the speed limit. The default of 900 km/h matches the cruising speed of commercial aircraft \u2014 anything at or above this speed is physically impossible and could indicate compromised credentials."), /* @__PURE__ */ React.createElement("p", null, "Source messages must contain a ", /* @__PURE__ */ React.createElement("b", null, "user identity field"), " and a ", /* @__PURE__ */ React.createElement("b", null, "geo-coordinates field"), " (", /* @__PURE__ */ React.createElement("code", null, "source_geo_coordinates"), ") for detection to work. Geo-enrichment can be configured via a GeoIP lookup pipeline rule or processor.")), /* @__PURE__ */ React.createElement(
      ImpossibleTravelFieldCheck,
      {
        userField: subtypeConfig.user_field,
        query: subtypeConfig.query,
        streams: (_d = eventDefinition.config.streams) != null ? _d : EMPTY_STRINGS,
        streamCategories: (_e = eventDefinition.config.stream_categories) != null ? _e : EMPTY_STRINGS,
        filters: eventDefinition.config.filters,
        lookbackMs: subtypeConfig.lookback_ms
      }
    ));
  }
  if (section !== "config") {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "impossible-travel-user-field",
      name: "user_field",
      label: "User Field",
      type: "text",
      required: true,
      value: subtypeConfig.user_field,
      onChange: handleSubtypeInputChange,
      help: "Message field containing the user identity (e.g. user_name, source_user)."
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-distance-threshold" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Distance Threshold"), /* @__PURE__ */ React.createElement(InputGroup, { style: { display: "flex" } }, /* @__PURE__ */ React.createElement(
    FormControl,
    {
      id: "impossible-travel-distance-threshold",
      name: "distance_threshold",
      type: "number",
      min: 1,
      step: 1,
      required: true,
      value: subtypeConfig.distance_threshold,
      onChange: (e) => updateSubtypeConfig(
        "distance_threshold",
        FormsUtils.getValueFromInput(e.target)
      )
    }
  ), /* @__PURE__ */ React.createElement(
    DropdownButton,
    {
      id: "impossible-travel-distance-unit",
      title: subtypeConfig.distance_unit === "miles" ? "miles" : "kilometers"
    },
    /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        active: subtypeConfig.distance_unit === "km",
        onSelect: () => updateSubtypeConfig("distance_unit", "km")
      },
      "kilometers"
    ),
    /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        active: subtypeConfig.distance_unit === "miles",
        onSelect: () => updateSubtypeConfig("distance_unit", "miles")
      },
      "miles"
    )
  )), /* @__PURE__ */ React.createElement(HelpBlock, null, "Minimum distance between consecutive logins required to trigger an alert. Also filters out noise from geo-IP inaccuracy.")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-time-threshold" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Time Threshold",
      update: handleTimeThresholdChange,
      value: timeThresholdDuration,
      unit: timeThresholdUnit,
      units: TIME_THRESHOLD_UNITS,
      required: true
    }
  ), /* @__PURE__ */ React.createElement("span", { className: "help-block" }, "Together with the distance threshold, defines the maximum travel speed. Logins exceeding this speed over the distance threshold will trigger an alert.")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-lookback" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Lookback Period",
      update: handleLookbackChange,
      value: lookbackDuration,
      unit: lookbackUnit,
      units: LOOKBACK_UNITS,
      required: true
    }
  ), /* @__PURE__ */ React.createElement("span", { className: "help-block" }, "How far back to search for a previous login when comparing locations.")));
};
ImpossibleTravelEventForm.defaultSubtypeConfig = initialImpossibleTravelConfig;
export default ImpossibleTravelEventForm;
